import React, { useState, useEffect } from "react"
import {
  Card,
  CardImg,
  CardBody,
  CardTitle,
  CardSubtitle,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Alert,
  Button,
  Spinner
} from "reactstrap"
import { Link } from "react-router-dom"
import { Auth } from "aws-amplify"
import traindexLogo from "../../images/traindex-logo.png"
import "./resetPassword.css"

const renderSuccessMessage = () => (
  <Alert color="success">
    <h4 className="alert-heading">Success!</h4>
    <p>Your password has been reset.</p>
    <hr />
    <p>
      <Link to="/sign-in">Click here to sign in with your new credentials.</Link>
    </p>
  </Alert>
)

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    code: "",
    email: "",
    password: "",
    confirmPassword: ""
  })
  const [error, setError] = useState("")
  const [codeSent, setCodeSent] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)

  useEffect(() => {
    let interval = null
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [resendTimer])

  const handleSendCodeClick = async event => {
    event.preventDefault()

    try {
      error && setError("")
      setIsSendingCode(true)
      await Auth.forgotPassword(formData.email)
      setIsSendingCode(false)
      setCodeSent(true)
      setResendTimer(60)
    } catch (error) {
      setError(error.message)
      setIsSendingCode(false)
    }
  }

  const handleResendCode = async event => {
    event.preventDefault()

    try {
      error && setError("")
      setIsSendingCode(true)
      await Auth.forgotPassword(formData.email)
      setIsSendingCode(false)
      setResendTimer(60)
    } catch (error) {
      setError(error.message)
      setIsSendingCode(false)
    }
  }

  const handleConfirmClick = async event => {
    event.preventDefault()

    try {
      if (formData.code.length < 6) {
        throw Error("Invalid confirmation code!")
      }
      if (formData.password.length < 10) {
        throw Error("Password must have at least 10 characters!")
      }
      if (formData.password !== formData.confirmPassword) {
        throw Error("Password and Confirm Password does not match!")
      }
      error && setError("")
      setIsConfirming(true)
      await Auth.forgotPasswordSubmit(formData.email, formData.code, formData.password)
      setIsConfirming(false)
      setConfirmed(true)
    } catch (error) {
      setError(error.message)
      setIsConfirming(false)
    }
  }

  const onChangeHandler = event => {
    const { name, value } = event.target
    if (Object.prototype.hasOwnProperty.call(formData, name)) {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const renderRequestCodeForm = () => (
    <Form onSubmit={handleSendCodeClick} className="d-flex flex-column">
      <FormGroup>
        <Label for="email" className="font-weight-bold input-label text-dark">
          EMAIL <span>*</span>
        </Label>
        <Input
          type="email"
          name="email"
          id="email"
          placeholder="e.g. example@domain.com"
          onChange={onChangeHandler}
          value={formData.email || ""}
          required
        />
      </FormGroup>
      <Button
        type="submit"
        disabled={isSendingCode}
        className="mt-3 px-3 font-weight-bold d-flex align-self-center confirm-btn"
      >
        {isSendingCode ? <Spinner size="sm" className="m-1" /> : null}
        SEND CONFIRMATION
      </Button>
    </Form>
  )

  const renderConfirmationForm = () => (
    <Form onSubmit={handleConfirmClick} className="d-flex flex-column">
      <FormGroup> 
        <Label for="code" className="font-weight-bold input-label text-dark">
          CODE <span>*</span>
        </Label>
        <Input
          type="text"
          name="code"
          id="code"
          placeholder="e.g. 123456"
          onChange={onChangeHandler}
          value={formData.code || ""}
          required
        />
        <FormText color="muted">Please check your email for the confirmation code.</FormText>
        <Button
          color="link"
          className="resend-code p-0 always-underline"
          onClick={handleResendCode}
          disabled={isSendingCode || resendTimer > 0}
        >
          {isSendingCode ? "Resending..." : resendTimer > 0 ? `Resend Code (${resendTimer}s)` : "Resend Code"}
        </Button>


      </FormGroup>
      <FormGroup>
        <Label for="password" className="font-weight-bold input-label text-dark">
          NEW PASSWORD <span>*</span>
        </Label>
        <Input
          type="password"
          name="password"
          id="password"
          onChange={onChangeHandler}
          value={formData.password || ""}
          minLength="10"
          required
        />
        <FormText>Must have at least 10 characters</FormText>
      </FormGroup>
      <FormGroup>
        <Label for="confirmPassword" className="font-weight-bold input-label text-dark" disabled={isSendingCode}>
          CONFIRM PASSWORD <span>*</span>
        </Label>
        <Input
          type="password"
          name="confirmPassword"
          id="confirmPassword"
          onChange={onChangeHandler}
          value={formData.confirmPassword || ""}
          minLength="10"
          required
        />
      </FormGroup>
      <Button 
        type="submit" 
        disabled={isConfirming} 
        className="mt-3 px-3 font-weight-bold d-flex align-self-center confirm-btn"
      >
        {isConfirming ? <Spinner size="sm" className="m-1" /> : null}
        CONFIRM
      </Button>
    </Form>
  )

  return (
    <div className="d-flex justify-content-center align-items-center full_height purple_background">
      <Card className="card-container p-2 my-3">
        <Link to="/" className="align-self-center mt-2 mb-3">
          <CardImg className="card_img" top src={traindexLogo} alt="Traindex logo" />
        </Link>
        <CardTitle className="card_title font-weight-bold align-self-center mt-2">
          RESET PASSWORD
        </CardTitle>
        {error ? <CardSubtitle className="align-self-center mt-2 text-danger">{error}</CardSubtitle> : null}
        <CardBody className="card_body align-self-center mx-5">
          {!codeSent ? renderRequestCodeForm() : !confirmed ? renderConfirmationForm() : renderSuccessMessage()}
        </CardBody>
      </Card>
    </div>
  )
}

export default ResetPassword